import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import QueryCard from '../components/QueryCard';
import { 
  PlusCircle, FileQuestion, CheckCircle, 
  Clock, AlertCircle, User, Mail, Building 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [queries, setQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api
      .get('/queries')
      .then((res) => setQueries(res.data))
      .catch(() => setQueries([]))
      .finally(() => setLoadingQueries(false));
  }, []);

  const getFilteredQueries = () => {
    if (filter === 'all') return queries;
    if (filter === 'pending') return queries.filter(q => q.status === 'Pending');
    if (filter === 'in-progress') return queries.filter(q => q.status === 'In Progress');
    if (filter === 'resolved') return queries.filter(q => q.status === 'Resolved');
    return queries;
  };

  const filteredQueries = getFilteredQueries();
  const pendingCount = queries.filter(q => q.status === 'Pending').length;
  const inProgressCount = queries.filter(q => q.status === 'In Progress').length;
  const resolvedCount = queries.filter(q => q.status === 'Resolved').length;

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#003366] mb-2">
            Welcome back, {user.fullName}!
          </h1>
          <p className="text-gray-600">
            Manage your queries and track their progress
          </p>
        </div>

        {/* Free Query Alert */}
        {!user.freeQueryUsed && (
          <Alert className="mb-8 bg-green-50 border-green-200">
            <AlertCircle className="h-5 w-5 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>You have 1 free query available!</strong> Submit your first query at no cost with our standard 24-hour SLA.
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Queries</p>
                  <p className="text-3xl font-bold text-[#003366] mt-1">{queries.length}</p>
                </div>
                <FileQuestion className="h-12 w-12 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-3xl font-bold text-[#003366] mt-1">{pendingCount}</p>
                </div>
                <AlertCircle className="h-12 w-12 text-yellow-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">In Progress</p>
                  <p className="text-3xl font-bold text-[#003366] mt-1">{inProgressCount}</p>
                </div>
                <Clock className="h-12 w-12 text-blue-600 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Resolved</p>
                  <p className="text-3xl font-bold text-[#003366] mt-1">{resolvedCount}</p>
                </div>
                <CheckCircle className="h-12 w-12 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Button
            onClick={() => navigate('/submit-query')}
            className="bg-[#0078D4] hover:bg-[#005a9e] h-12 font-semibold flex items-center space-x-2"
          >
            <PlusCircle className="h-5 w-5" />
            <span>Submit New Query</span>
          </Button>
        </div>

        {/* Queries Section */}
        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <Tabs value={filter} onValueChange={setFilter} className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="all">
                  All ({queries.length})
                </TabsTrigger>
                <TabsTrigger value="pending">
                  Pending ({pendingCount})
                </TabsTrigger>
                <TabsTrigger value="in-progress">
                  In Progress ({inProgressCount})
                </TabsTrigger>
                <TabsTrigger value="resolved">
                  Resolved ({resolvedCount})
                </TabsTrigger>
              </TabsList>

              <TabsContent value={filter}>
                {loadingQueries ? (
                  <div className="text-center py-12 text-gray-500" data-testid="queries-loading">Loading queries...</div>
                ) : filteredQueries.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredQueries.map((query) => (
                      <QueryCard key={query.id} query={query} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileQuestion className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No queries found
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {filter === 'all' 
                        ? "You haven't submitted any queries yet."
                        : `No ${filter.replace('-', ' ')} queries.`}
                    </p>
                    {filter === 'all' && (
                      <Button
                        onClick={() => navigate('/submit-query')}
                        className="bg-[#0078D4] hover:bg-[#005a9e]"
                      >
                        Submit Your First Query
                      </Button>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* User Info Card */}
        <Card className="mt-8 shadow-lg">
          <CardContent className="pt-6">
            <h3 className="text-xl font-bold text-[#003366] mb-4">Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium text-gray-900">{user.fullName}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-gray-900">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Company</p>
                  <p className="font-medium text-gray-900">{user.companyName || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Free Query Status</p>
                  <p className="font-medium text-gray-900">
                    {user.freeQueryUsed ? 'Used' : 'Available'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
